import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
} from '@mui/material';
import { CheckCircleOutline, HighlightOff, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import AppStyles from '../styles/AppStyles';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function VetSignUpPage() {
  const { t } = useTranslation();
  const query = useQuery();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // TEMPORARY: Restrict access to signup page - redirect to login
  useEffect(() => {
    navigate('/login', { replace: true });
  }, [navigate]);

  // Redirect to dashboard if user is already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: query.get('email') || '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false,
    confirmPassword: false,
  });

  // Password validation rules (matching mobile SignUpScreen.js)
  const password = formData.password;
  const isLengthValid = () => password.length >= 6 && password.length <= 20;
  const hasLetter = () => /[a-zA-Z]/.test(password);
  const hasLowercase = () => /[a-z]/.test(password);
  const hasUppercase = () => /[A-Z]/.test(password);
  const hasNumber = () => /\d/.test(password);
  const hasSymbol = () => /[!@$%^&*+#]/.test(password);

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (fieldErrors[e.target.name]) {
      setFieldErrors(prev => ({ ...prev, [e.target.name]: false }));
    }
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    const errs = {
      firstName: !formData.firstName?.trim(),
      lastName: !formData.lastName?.trim(),
      email: !formData.email || !formData.email.includes('@'),
      password: !formData.password,
      confirmPassword: !formData.confirmPassword || formData.confirmPassword !== formData.password,
    };
    setFieldErrors(errs);
    const pwOk = isLengthValid() && hasLetter() && hasLowercase() && hasUppercase() && hasNumber() && hasSymbol();
    return !errs.firstName && !errs.lastName && !errs.email && !errs.password && !errs.confirmPassword && pwOk;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const token = query.get('token');
      if (!token) {
        setLoading(false);
        setError(t('vetSignUp.invalidInviteLink'));
        return;
      }
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';
      const endpoint = `${API_BASE}/api/auth/signup-vet-invite`;
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
        })
      });
      let data;
      try {
        data = await resp.json();
      } catch (_) {
        // Fallback to text if not json
        const txt = await resp.text();
        data = { msg: txt };
      }
      if (!resp.ok || !data?.success) {
        const msg = data?.msg || t('vetSignUp.signupFailed');
        console.error('Vet signup failed:', { endpoint, status: resp.status, data });
        throw new Error(msg);
      }
      setSuccess(t('vetSignUp.accountCreated'));
      setLoading(false);
      setTimeout(() => navigate('/login'), 900);
    } catch (err) {
      setLoading(false);
      console.error('Vet signup error:', err);
      setError(err?.message || t('vetSignUp.unexpectedError'));
    }
  };

  const Requirement = ({ ok, children }) => (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      {ok ? (
        <CheckCircleOutline fontSize="small" sx={{ color: AppStyles.colors.success.main }} />
      ) : (
        <HighlightOff fontSize="small" sx={{ color: AppStyles.colors.error.main }} />
      )}
      <Typography variant="body2" sx={{ color: ok ? AppStyles.colors.success.main : AppStyles.colors.text.secondary }}>
        {children}
      </Typography>
    </Stack>
  );

  const inviteEmail = query.get('email');

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${AppStyles.colors.background.default} 0%, ${AppStyles.colors.background.secondary} 100%)`,
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Don't render signup form if already authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${AppStyles.colors.background.default} 0%, ${AppStyles.colors.background.secondary} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={8}
          sx={{
            padding: { xs: 3, sm: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            borderRadius: 3,
            background: `linear-gradient(145deg, ${AppStyles.colors.background.paper} 0%, ${AppStyles.colors.background.secondary} 100%)`,
            border: `1px solid ${AppStyles.colors.border.light}`,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`,
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', position: 'relative' }}>
              <Box sx={{ position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', width: '60px', height: '2px', background: `linear-gradient(90deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.secondary.main} 100%)`, borderRadius: 1 } }}>
                <img src="/images/logo/textAndIconColor.png" alt="Pawwi Logo" style={{ height: '40px', width: 'auto', maxWidth: '320px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }} />
              </Box>
            </Box>
            <Typography component="h1" variant="h3" gutterBottom sx={{ color: AppStyles.colors.primary.dark, fontWeight: AppStyles.typography.fontWeight.bold, textAlign: 'center', mb: 1 }}>
              {t('vetSignUp.title')}
            </Typography>
            {inviteEmail && (
              <Typography variant="body2" sx={{ color: AppStyles.colors.text.secondary }}>
                {t('vetSignUp.invitationFor')}<strong>{inviteEmail}</strong>
              </Typography>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%', mt: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="firstName"
                label={t('vetSignUp.firstName')}
                name="firstName"
                autoComplete="given-name"
                autoFocus
                value={formData.firstName}
                onChange={handleChange}
                error={fieldErrors.firstName}
                helperText={fieldErrors.firstName ? t('vetSignUp.firstNameRequired') : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          px: 0.5,
                          py: 0,
                          color: AppStyles.colors.text.secondary,
                          fontWeight: AppStyles.typography.fontWeight.medium,
                          fontSize: AppStyles.typography.fontSize.sm,
                        }}
                      >
                        {t('vetSignUp.drPrefix')}
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, mb: 2, flex: 1 }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="lastName"
                label={t('vetSignUp.lastName')}
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                error={fieldErrors.lastName}
                helperText={fieldErrors.lastName ? t('vetSignUp.lastNameRequired') : ''}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, mb: 2, flex: 1 }}
              />
            </Stack>

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('vetSignUp.email')}
              name="email"
              type="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              error={fieldErrors.email}
              helperText={fieldErrors.email ? t('vetSignUp.validEmailRequired') : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('vetSignUp.password')}
              type="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              error={fieldErrors.password}
              helperText={fieldErrors.password ? t('vetSignUp.passwordRequired') : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={t('vetSignUp.confirmPassword')}
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={fieldErrors.confirmPassword}
              helperText={fieldErrors.confirmPassword ? t('vetSignUp.passwordsMustMatch') : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, mb: 2 }}
            />

            {/* Password Requirements */}
            <Box sx={{
              background: AppStyles.colors.background.paper,
              borderRadius: 2,
              border: `1px solid ${AppStyles.colors.border.light}`,
              p: 2,
              mb: 3,
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: AppStyles.typography.fontWeight.medium, mb: 1, color: AppStyles.colors.cardTitle }}>
                {t('vetSignUp.passwordRequirements')}
              </Typography>
              <Requirement ok={isLengthValid()}>{t('vetSignUp.requirements.length')}</Requirement>
              <Requirement ok={hasLetter()}>{t('vetSignUp.requirements.letter')}</Requirement>
              <Requirement ok={hasLowercase()}>{t('vetSignUp.requirements.lowercase')}</Requirement>
              <Requirement ok={hasUppercase()}>{t('vetSignUp.requirements.uppercase')}</Requirement>
              <Requirement ok={hasNumber()}>{t('vetSignUp.requirements.number')}</Requirement>
              <Requirement ok={hasSymbol()}>{t('vetSignUp.requirements.symbol')}</Requirement>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: AppStyles.typography.fontSize.lg,
                fontWeight: AppStyles.typography.fontWeight.medium,
                textTransform: 'none',
                boxShadow: AppStyles.shadows.lg,
                background: `linear-gradient(135deg, ${AppStyles.colors.primary.main} 0%, ${AppStyles.colors.primary.light} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${AppStyles.colors.primary.dark} 0%, ${AppStyles.colors.primary.main} 100%)`,
                  boxShadow: AppStyles.shadows.xl,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              {loading ? t('vetSignUp.creatingAccount') : t('vetSignUp.createAccount')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}


